import { Component, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { CRMType } from 'src/app/user-profile/shared/models';

import { CrmOrganizationsService } from './crm-organizations.service';
import { CrmAccountLinked } from './shared/crm-account-linked.model';
import { CrmOrganization } from './shared/crm-organization.model';

@Component({
  selector: 'app-crm-organizations',
  templateUrl: './crm-organizations.component.html',
  styleUrls: ['./crm-organizations.component.scss'],
})
export class CrmOrganizationsComponent implements OnInit, OnDestroy {
  @Input() organizations: CrmOrganization[] = [];

  error!: string;
  alreadyLinkedSitesIdsByOrgId: { [key: string]: Set<number> };
  availableSitesToLinkForOrgId: Choice[] = [];

  sitesById: { type: string; typeName: string; name: string };
  crmType: CRMType;
  sites: SiteV2[];
  siteSelectedControl = new FormControl(null, Validators.required);
  loginUrlControl = new FormControl(null, Validators.required);

  @ViewChild('connectCrmOrganizationTemplateRef', { static: true })
  private connectCrmOrganizationTemplateRef: TemplateRef<any>;
  @ViewChild('chooseSiteToLinkTemplateRef', { static: true }) private chooseSiteToLinkTemplateRef: TemplateRef<any>;

  constructor(
    public sitesService: SitesService,
    private basicService: BasicService,
    private dialog: MatDialog,
    private crmOrganizationsService: CrmOrganizationsService,
    @Inject('DropdownItemsFactory') private dropdownItemsService: any,
    @Inject('$state') private $state: any,
    @Inject('TopToolbarFactory') private topToolbarService: any,
    @Inject('$stateParams') $stateParams: any
  ) {
    const { crmType, error } = $stateParams;
    this.crmType = crmType;

    topToolbarService.setTitleTranslation(`${crmType}/organizations/title`, null);
    topToolbarService.setButton('crm/organizations/create', this.startAuthorizeFlow, null);

    this.error = basicService.translate(error);

    this.alreadyLinkedSitesIdsByOrgId = {};

    this.sitesById = {} as {
      type: string;
      typeName: string;
      name: string;
    };

    for (const { id, type, type_name, name } of sitesService.sitesV2) {
      this.sitesById[id] = {
        type,
        typeName: type_name,
        name,
      };
    }
  }

  ngOnInit(): void {
    this.organizations.forEach(organization => {
      organization.linked_sites = organization.linked_sites
        .filter(({ site_id }) => this.sitesService.sitesV2Map.has(site_id))
        .sort((a, b) => {
          const siteA = this.sitesService.sitesV2Map.get(a.site_id);
          const siteB = this.sitesService.sitesV2Map.get(b.site_id);
          const compare = siteA.type - siteB.type;
          if (compare === 0) {
            return (siteA.name || '').localeCompare(siteB.name || '');
          }
          return compare;
        });
      const siteIds = organization.linked_sites.map(site => site.site_id);
      this.alreadyLinkedSitesIdsByOrgId[organization.id] = new Set(siteIds);
    });
  }

  private startAuthorizeFlow = () => {
    const crm = this.crmOrganizationsService.getStrategy(this.crmType);

    this.loginUrlControl.setValue(crm.getDefaultLoginUrl());

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      data: {
        title: this.basicService.translate('crm/organizations/login-url'),
        templateRef: this.connectCrmOrganizationTemplateRef,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      const loginUrl = this.loginUrlControl.value;
      if (result && loginUrl) {
        this.crmOrganizationsService.authorize(loginUrl, this.crmType).subscribe({
          next: ({ to_url }: { to_url: string }) => {
            window.location.href = to_url;
          },
        });
      }
    });
  };

  chooseSiteToLink(organization) {
    this.availableSitesToLinkForOrgId = this.dropdownItemsService.buildSiteItems(
      this.sitesService.sitesV2.filter(site => !this.alreadyLinkedSitesIdsByOrgId[organization.id].has(site.id))
    );
    if (this.availableSitesToLinkForOrgId.length) {
      this.siteSelectedControl.setValue(this.availableSitesToLinkForOrgId[0].id);
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      autoFocus: false,
      data: {
        title: this.basicService.translate('crm/organizations/select-site'),
        templateRef: this.chooseSiteToLinkTemplateRef,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
        disable: () => this.siteSelectedControl.invalid,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const selectedSiteId = this.siteSelectedControl.value;
        this.crmOrganizationsService
          .linkSiteToOrganization(organization.id, selectedSiteId)
          .pipe(finalize(() => this.siteSelectedControl.setValue(null)))
          .subscribe({
            next: response => {
              this.basicService.toastSuccess('crm/organizations/sites/link-success');
              organization.linked_sites.push(response);
              this.alreadyLinkedSitesIdsByOrgId[organization.id].add(selectedSiteId);
            },
          });
      } else {
        this.siteSelectedControl.setValue(null);
      }
    });
  }

  reAuthorize(organization) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      data: {
        description: this.basicService.translate('crm/organizations/confirm-reauthorize'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.crmOrganizationsService.authorize(organization.instance_url, this.crmType, organization.id).subscribe({
          next: ({ to_url }: { to_url: string }) => {
            window.location.href = to_url;
          },
        });
      }
    });
  }

  updateLinkedSite(organizationId, linkedSite: CrmAccountLinked) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      data: {
        description: this.basicService.translate(
          linkedSite.is_active ? 'crm/organizations/sites/confirm-enable' : 'crm/organizations/sites/confirm-disable'
        ),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.crmOrganizationsService.updateLinkedSite(organizationId, linkedSite).subscribe({
          next: () => {
            if (linkedSite.is_active) {
              this.basicService.toastSuccess('crm/organizations/sites/enable-success');
            } else {
              this.basicService.toastSuccess('crm/organizations/sites/disable-success');
            }
          },
        });
      } else {
        // Discard the model changes.
        linkedSite.is_active = !linkedSite.is_active;
      }
    });
  }

  deleteLinkedSite(organization, linkedSite) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      data: {
        description: this.basicService.translate('crm/organizations/sites/confirm-deletion'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const selectedSiteId = linkedSite.id;
        this.crmOrganizationsService.deleteLinkedSite(organization.id, selectedSiteId).subscribe({
          next: () => {
            this.basicService.toastSuccess('crm/organizations/sites/delete-success');
            const linkedSiteIndex = organization.linked_sites.indexOf(linkedSite);
            organization.linked_sites.splice(linkedSiteIndex, 1);
            this.alreadyLinkedSitesIdsByOrgId[organization.id].delete(linkedSite.site_id);
          },
        });
      }
    });
  }

  deleteOrganization(organization) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      data: {
        description: this.basicService.translate('crm/organizations/confirm-deletion'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.crmOrganizationsService.deleteOrganization(organization.id).subscribe({
          next: () => {
            this.basicService.toastSuccess('crm/organizations/delete-success');
            const organizationIndex = this.organizations.indexOf(organization);
            this.organizations.splice(organizationIndex, 1);
          },
        });
      }
    });
  }

  editOrganization(organizationId) {
    this.$state.go('auth.alcmeon.fuse.parameters.crm.organizations.configuration', { organizationId });
  }

  ngOnDestroy(): void {
    this.topToolbarService.clearButtons();
  }
}
